"use client";

import React, { useEffect, useRef } from 'react';

export default function AboutSection() {
  const sectionRef = useRef<HTMLElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            entry.target.querySelectorAll('.reveal-hidden').forEach((el, i) => {
              setTimeout(() => el.classList.add('revealed'), i * 120);
            });
          }
        });
      },
      { threshold: 0.15 }
    );
    if (sectionRef?.current) observer?.observe(sectionRef?.current);
    return () => observer?.disconnect();
  }, []);

  return (
    <section
      id="about"
      ref={sectionRef}
      className="section-padding relative overflow-hidden"
      aria-labelledby="about-heading"
    >
      {/* Subtle background glow */}
      <div
        className="pointer-events-none absolute top-0 right-0 w-[600px] h-[400px]"
        aria-hidden="true"
        style={{
          background: 'radial-gradient(ellipse, rgba(0,40,100,0.15) 0%, transparent 70%)',
          filter: 'blur(80px)',
        }}
      />
      <div className="max-w-7xl mx-auto px-5 lg:px-12 relative z-10">
        {/* Teal divider top */}
        <div className="teal-line mb-10 md:mb-16 reveal-hidden" />

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-20">
          {/* Left — Section header */}
          <div className="lg:col-span-4">
            <div className="reveal-hidden">
              <div className="section-label mb-5">01 / About</div>
              <h2
                id="about-heading"
                className="text-seafoam"
                style={{
                  fontFamily: 'DM Sans, sans-serif',
                  fontSize: 'clamp(2rem, 4vw, 3.5rem)',
                  fontWeight: 700,
                  lineHeight: 1.0,
                  letterSpacing: '-0.03em',
                }}
              >
                About
              </h2>
            </div>

            {/* Decorative — depth coordinate — hidden on mobile */}
            <div
              className="mt-10 reveal-hidden hidden lg:block"
              style={{
                fontFamily: 'DM Mono, monospace',
                fontSize: '0.58rem',
                letterSpacing: '0.15em',
                color: 'rgba(0,212,200,0.3)',
                lineHeight: 2,
              }}
            >
              <div>PLATFORM TYPE</div>
              <div style={{ color: 'rgba(0,212,200,0.6)' }}>Autonomous Survey AUV</div>
              <div className="mt-2">DEPLOYMENT ENV</div>
              <div style={{ color: 'rgba(0,212,200,0.6)' }}>Offshore & Coastal Domains</div>
              <div className="mt-2">COVERAGE MODE</div>
              <div style={{ color: 'rgba(0,212,200,0.6)' }}>High-efficiency acquisition</div>
            </div>
          </div>

          {/* Right — Body text */}
          <div className="lg:col-span-8 flex flex-col justify-center">
            <p
              className="reveal-hidden"
              style={{
                fontSize: 'clamp(1rem, 1.5vw, 1.2rem)',
                lineHeight: 1.8,
                color: 'rgba(232, 237, 244, 0.85)',
                fontWeight: 300,
                maxWidth: '640px',
              }}
            >
              Pelagic Robotics is developing a survey-focused autonomous platform designed to improve corridor coverage efficiency and deliver repeatable datasets.
            </p>
            <p
              className="mt-6 reveal-hidden"
              style={{
                fontSize: 'clamp(0.95rem, 1.3vw, 1.1rem)',
                lineHeight: 1.85,
                color: 'rgba(232, 237, 244, 0.85)',
                fontWeight: 300,
                maxWidth: '600px',
              }}
            >
              Our approach is built around practical deployment, reliable acquisition, and deliverables that integrate cleanly with client processing and reporting requirements.
            </p>

          </div>
        </div>

      </div>
    </section>
  );
}